// ---------------------------------------------------------------------------
// Map layer styles, shared between SubBasinMap.tsx (to paint them) and
// Legend.tsx (to explain what each one means) — a single place so colors
// aren't duplicated.
// ---------------------------------------------------------------------------

export const AQUIFER_STYLE = {
  color: "#5b3fa0",
  weight: 1,
  fillColor: "#8f7bc9",
  fillOpacity: 0.25,
};

export const OUTLET_POINT_STYLE = { color: "#1b4d5c", fillColor: "#3fa7b3", fillOpacity: 0.9 };

/** Selection highlight fill color — shared between subBasinMapStyle.ts's `SELECTION_HIGHLIGHT` (border/fill for a plain sub-basin) and SnowPatterns.tsx's `pattern-selected` (fill for a sub-basin that also has a snow zone, see that file's own comment on why selection can't just reuse a plain fillColor there). */
export const SELECTION_FILL_COLOR = "#e0913f";

export const BASE_STYLE = {
  color: "#1b4d5c",
  weight: 1,
  fillColor: "#3fa7b3",
  fillOpacity: 0.35,
};

/** Sub-basin with something pending (no zone, no outlet, no own series...) — will be excluded from the calculation, painted gray. */
export const PROBLEM_STYLE = {
  color: "#3f4650",
  weight: 1,
  fillColor: "#8a92a0",
  fillOpacity: 0.6,
};

/** Shapefile geometry with no project sub-basin behind it (gisCode with no match) — not "normal" nor "with issues", it's simply not part of the model. Dashed border and muted fill so it isn't mistaken for a real sub-basin. */
export const UNLINKED_STYLE = {
  color: "#9a9a9a",
  weight: 1,
  dashArray: "4",
  fillColor: "#d8d8d8",
  fillOpacity: 0.25,
};

/** MITECO's official sub-basins (MitecoSubBasinsLayer.tsx) — a reference overlay, unrelated to the project's own data, so deliberately unfilled and in a color that doesn't collide with BASE_STYLE/PROBLEM_STYLE/UNLINKED_STYLE. */
export const MITECO_SUBBASIN_STYLE = {
  color: "#c17817",
  weight: 1,
  fillOpacity: 0,
};

/** MITECO's official (river) basins (MitecoBasinsLayer.tsx) — the coarser level, one per hydrographic basin instead of per sub-basin. Thicker and in the same blue INSPIRE/IGN used for this before, so it reads as "the big regional boundary" against MITECO_SUBBASIN_STYLE's thinner orange. */
export const MITECO_BASINS_STYLE = {
  color: "#1d387a",
  weight: 2,
  fillOpacity: 0,
};

/** A MITECO polygon already imported into this project (MitecoSelectableLayer.tsx, MapMitecoMode.alreadyAdded) — muted/dashed like UNLINKED_STYLE and not clickable, so it reads as "nothing to do here" rather than "an option you could still pick". */
export const MITECO_ALREADY_ADDED_STYLE = {
  color: "#9a9a9a",
  weight: 1,
  dashArray: "4",
  fillColor: "#d8d8d8",
  fillOpacity: 0.35,
};

// ---------------------------------------------------------------------------
// AEMET weather stations (AemetStationsLayer.tsx) — shown while at least one
// "Fetch from AEMET" job is tracked (aemetJobStore.ts), so the candidate
// stations' real position relative to the whole network is visible, not
// just their name/distance as text in the dialog.
// ---------------------------------------------------------------------------

/** Every other AEMET station, tiny and muted — there purely for context (~920 of them), not interactive. */
export const AEMET_STATION_STYLE = {
  color: "#9a9a9a",
  weight: 1,
  fillColor: "#9a9a9a",
  fillOpacity: 0.6,
};

/** A job's main (nearest) candidate station — the one that'll actually be used unless it has a gap some day. */
export const AEMET_MAIN_STATION_STYLE = {
  color: "#7a1f1f",
  weight: 2,
  fillColor: "#c0392b",
  fillOpacity: 0.9,
};

/** A job's backup candidate stations — only used to fill gaps in the main one's data. */
export const AEMET_BACKUP_STATION_STYLE = {
  color: "#8a5215",
  weight: 2,
  fillColor: "#e0913f",
  fillOpacity: 0.85,
};
